import { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';

import type { AuditConfigSection } from '../../api/audit-config-queries';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Section title with an "Unsaved changes" indicator. */
export function SectionTitle({ title, isDirty }: { title: string; isDirty: boolean }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
      <Typography variant="h6" sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
        {title}
      </Typography>
      {isDirty && <Chip label="Unsaved changes" size="small" color="warning" />}
    </Box>
  );
}

/** Load / conflict / validation alerts shown above the editor. */
export function SectionAlerts({
  loadError,
  hasConflict,
  validationError,
  onDiscard,
}: {
  loadError: string | null;
  hasConflict: boolean;
  validationError: string | null;
  onDiscard: () => void;
}) {
  return (
    <>
      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load settings — {loadError}
        </Alert>
      )}
      {hasConflict && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={onDiscard}>
              Load latest
            </Button>
          }
        >
          This section was changed on the server since you started editing. Saving will replace that
          change.
        </Alert>
      )}
      {validationError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationError}
        </Alert>
      )}
    </>
  );
}

/** "Last updated … by …" provenance line. */
export function SectionProvenance({ record }: { record?: AuditConfigSection<unknown> }) {
  if (!record?.updatedAt) return null;
  return (
    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
      Last updated: {formatDate(record.updatedAt)}
      {record.updatedBy ? ` by ${record.updatedBy}` : ''}
    </Typography>
  );
}

/** Success toast after a verified save. */
export function SaveSuccessSnackbar({
  message,
  onClose,
}: {
  message: string | null;
  onClose: () => void;
}) {
  return (
    <Snackbar
      open={message !== null}
      autoHideDuration={4000}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <Alert onClose={onClose} severity="success" variant="filled" sx={{ width: '100%' }}>
        {message}
      </Alert>
    </Snackbar>
  );
}

/**
 * Warns before losing unsaved edits: an in-app dialog for in-app navigation
 * (including switching Settings tabs) and the browser's beforeunload prompt for
 * reloads / tab closes.
 */
export function UnsavedChangesGuard({ when }: { when: boolean }) {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      when &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search)
  );

  useEffect(() => {
    if (!when) return;
    function handleBeforeUnload(event: BeforeUnloadEvent): void {
      event.preventDefault();
      // Legacy browsers require returnValue to be set to show the prompt
      event.returnValue = '';
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [when]);

  return (
    <Dialog open={blocker.state === 'blocked'} onClose={() => blocker.reset?.()}>
      <DialogTitle>Discard unsaved changes?</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          You have changes on this page that have not been applied. Leaving will discard them.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => blocker.reset?.()} color="inherit">
          Stay
        </Button>
        <Button onClick={() => blocker.proceed?.()} color="warning" variant="contained">
          Discard changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
